import {
  DEFAULT_WORKSPACE_THEME_ID,
  ensureWorkspaceAppThemeFontsLoaded,
  resolveUserAppTheme,
  resolveUserAppThemeDefinition,
  workspaceBrandThemeCssVars,
} from '~/lib/brandTheme';
import type { UserAppearancePreferences, WorkspaceThemeId } from '~/types/models';
import type { ThemePreference } from './useThemeMode';

export type AppDensity = 'compact' | 'default' | 'comfortable';

const APPEARANCE_STORAGE_KEY = 'vuey-user-appearance';

const DEFAULT_APPEARANCE: Required<Pick<UserAppearancePreferences, 'themeId' | 'colorMode' | 'density'>> = {
  themeId: DEFAULT_WORKSPACE_THEME_ID,
  colorMode: 'system',
  density: 'default',
};

function normalizeAppearance(value?: UserAppearancePreferences | null) {
  return {
    themeId: resolveUserAppThemeDefinition(value?.themeId).id as WorkspaceThemeId,
    colorMode: (['system', 'light', 'dark'].includes(String(value?.colorMode))
      ? value?.colorMode
      : DEFAULT_APPEARANCE.colorMode) as ThemePreference,
    density: (['compact', 'default', 'comfortable'].includes(String(value?.density))
      ? value?.density
      : DEFAULT_APPEARANCE.density) as AppDensity,
  };
}

function readCachedAppearance(): UserAppearancePreferences | null {
  if (!import.meta.client) return null;

  try {
    return JSON.parse(window.localStorage.getItem(APPEARANCE_STORAGE_KEY) || 'null');
  } catch {
    return null;
  }
}

export function useUserAppearance() {
  const userStore = useUserStore();
  const themeMode = useThemeMode();
  const cachedAppearance = useState<UserAppearancePreferences | null>('cached-user-appearance', readCachedAppearance);

  const appearance = computed(() => normalizeAppearance(
    userStore.user?.preferences?.appearance || cachedAppearance.value,
  ));
  const activeTheme = computed(() => resolveUserAppTheme(appearance.value.themeId));
  const activeThemeDefinition = computed(() => resolveUserAppThemeDefinition(appearance.value.themeId));
  const themeStyle = computed(() => workspaceBrandThemeCssVars(activeTheme.value));

  function applyAppearance() {
    if (!import.meta.client) return;

    const nextAppearance = appearance.value;
    const root = document.documentElement;
    Object.entries(themeStyle.value).forEach(([key, value]) => root.style.setProperty(key, value));
    root.dataset.density = nextAppearance.density;
    root.dataset.appTheme = nextAppearance.themeId;
    ensureWorkspaceAppThemeFontsLoaded(nextAppearance.themeId);
    themeMode.setPreference(nextAppearance.colorMode);
    window.localStorage.setItem(APPEARANCE_STORAGE_KEY, JSON.stringify(nextAppearance));
  }

  async function updateAppearance(patch: Partial<UserAppearancePreferences>) {
    const previous = appearance.value;
    const next = normalizeAppearance({ ...previous, ...patch });
    cachedAppearance.value = next;
    applyAppearance();

    try {
      await userStore.updateAppearancePreferences(next);
    } catch (error) {
      cachedAppearance.value = previous;
      applyAppearance();
      throw error;
    }
  }

  async function toggleColorMode() {
    await updateAppearance({ colorMode: themeMode.isDark.value ? 'light' : 'dark' });
  }

  watch(
    () => userStore.user?.preferences?.appearance,
    () => applyAppearance(),
    { deep: true, immediate: true },
  );

  if (import.meta.client) {
    onMounted(applyAppearance);
  }

  return {
    appearance,
    activeTheme,
    activeThemeDefinition,
    themeStyle,
    isDark: themeMode.isDark,
    updateAppearance,
    toggleColorMode,
    applyAppearance,
  };
}
